// src/api/walletService.js
// Customer-facing wallet helpers: balance subscription, pending top-up
// lookup/creation, and proof upload. Balance-mutating calls (verify/reject/
// pay-with-wallet) live on the backend and are called directly via fetch
// from the pages that need them — never here.
import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../utils/firebase';

// Used only until a salon has configured its own cash packages (admin: Packages tab → Cash Packages).
export const DEFAULT_WALLET_TOPUP_PACKAGES = [
  { payAmount: 100, creditAmount: 100 },
  { payAmount: 500, creditAmount: 500 },
  { payAmount: 1000, creditAmount: 1000 }
];

/**
 * A salon's admin-configured cash packages — each a {payAmount, creditAmount}
 * pair, e.g. pay $100 to be credited $110. Falls back to the 1:1 defaults
 * when the salon hasn't configured any yet.
 */
export async function getWalletTopupPackages(salonId) {
  const salonDoc = await getDoc(doc(db, 'salons', salonId));
  const configured = salonDoc.exists() ? salonDoc.data().walletTopupPackages : null;
  return Array.isArray(configured) && configured.length > 0 ? configured : DEFAULT_WALLET_TOPUP_PACKAGES;
}

// Alias for clarity — same function, preferred name going forward
export const getCashPackages = getWalletTopupPackages;

/**
 * Live-subscribe to a customer's wallet balance (and full customer doc, in
 * case the caller needs other fields too).
 */
export function subscribeToCustomer(customerId, callback) {
  const customerRef = doc(db, 'customers', customerId);
  return onSnapshot(customerRef, snap => {
    callback(snap.exists() ? snap.data() : null);
  });
}

/**
 * Find an existing pending top-up request that the customer has already
 * submitted proof for (i.e. actually awaiting admin review). A pending
 * request the customer never uploaded a screenshot for is treated as an
 * abandoned draft — it does not block starting a fresh top-up and is not
 * surfaced anywhere in the UI.
 */
export async function getPendingTopupForCustomer(customerEmail, salonId) {
  const q = query(
    collection(db, 'transactions'),
    where('customerEmail', '==', customerEmail),
    where('salonId', '==', salonId),
    where('type', '==', 'wallet_topup'),
    where('walletTopupStatus', '==', 'pending')
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;

  let latest = null;
  snap.forEach(d => {
    const data = { id: d.id, ...d.data() };
    if (!data.proofImageUrl) return; // no screenshot uploaded yet — not a real pending request
    if (!latest || (data.createdAt?.seconds || 0) > (latest.createdAt?.seconds || 0)) {
      latest = data;
    }
  });
  return latest;
}

/**
 * Upload the customer's payment-proof screenshot and, only once that upload
 * succeeds, create the wallet_topup transaction document with the proof
 * already attached — a single atomic step. This is deliberate: nothing is
 * ever written to the transactions collection for a top-up the customer
 * started but never actually paid/uploaded proof for, so an abandoned
 * attempt leaves no trace in Firebase at all.
 */
export async function createTopupTransactionWithProof({
  salonId, customerId, customerEmail, customerName, amount, creditAmount, reference, qrType, file
}) {
  if (!(amount > 0) || !(creditAmount > 0)) {
    throw new Error('Invalid top-up amount');
  }

  const path = `walletTopupProofs/${salonId}/${customerId}/${Date.now()}_${file.name}`;
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file);
  const proofImageUrl = await getDownloadURL(snapshot.ref);

  // The pay/credit pair is re-validated against the salon's configured
  // packages by the Firestore create rule — this call fails if it doesn't
  // match, so a tampered creditAmount can never be written.
  const docRef = await addDoc(collection(db, 'transactions'), {
    type: 'wallet_topup',
    salonId,
    clientId: `${salonId}-client`,
    customerId,
    customerEmail,
    customerName,
    amount,
    creditAmount,
    price: amount,
    totalAmount: amount,
    currency: 'SGD',
    paymentMethod: 'PayNow',
    status: 'pending',
    paymentStatus: 'Pending',
    walletTopupStatus: 'pending',
    proofImageUrl,
    proofUploadedAt: serverTimestamp(),
    paynowReference: reference || null,
    qrType: qrType || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return { transactionId: docRef.id, proofImageUrl };
}

/**
 * Fetch a customer's wallet-related transaction history (top-ups and
 * Wallet-paid purchases), most recent first.
 */
export async function getWalletTransactionHistory(customerEmail, salonId, max = 20) {
  // Only requests the customer actually submitted proof for count as "history"
  // — a pending request with no screenshot uploaded yet is an abandoned draft.
  const isReviewable = (t) => !!t.proofImageUrl || ['verified', 'rejected'].includes(t.walletTopupStatus);

  try {
    const q = query(
      collection(db, 'transactions'),
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId),
      where('type', '==', 'wallet_topup'),
      orderBy('createdAt', 'desc'),
      limit(max)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(isReviewable);
  } catch (err) {
    // Missing composite index fallback — same tolerance pattern used elsewhere in the app
    console.warn('Wallet history ordered query failed, falling back to unordered:', err);
    const q = query(
      collection(db, 'transactions'),
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId),
      where('type', '==', 'wallet_topup')
    );
    const snap = await getDocs(q);
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(isReviewable)
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      .slice(0, max);
  }
}
